<template>
  <div class="project-worklogs">
    <OmxPageHeader
        :title="pageTitle"
        :subtitle="headerSubtitle"
        :pills="headerPills"
        sticky
    >
      <template #actions>
        <b-button @click="backClicked" icon-left="arrow-left" tabindex="1">
          {{ $t('common.back') }}
        </b-button>
        <OmxPermissionButton
            :allowed="currentUserAccess('ProjectSummary', 'VIEW', project_id)"
            icon-left="chart-gantt"
            tag="router-link"
            :to="{ name: 'ProjectSummary', params: { project_id } }"
        >{{ $t('projects-view.show-summary') }}</OmxPermissionButton>
      </template>
    </OmxPageHeader>

    <OmxCard density="compact" class="project-worklogs__datebar">
      <div class="project-worklogs__datebar-inner">
        <div class="project-worklogs__date">
          <button
              class="project-worklogs__nav-btn"
              @click="shiftDate(-1)"
              :title="$t('project-logs.previous-day')"
          ><b-icon icon="chevron-left" size="is-small" /></button>
          <b-datepicker
              :mobile-native="false"
              v-model="modelDate"
              :placeholder="$t('project-logs.select-date')"
              icon="calendar-today"
              :locale="$i18n.locale === 'fr' ? 'fr-FR' : 'en-GB'"
              :max-date="maxSelectableDate"
              show-week-number
              position="is-bottom-right"
              @input="dateSelectClicked"
          />
          <button
              class="project-worklogs__nav-btn"
              @click="shiftDate(1)"
              :disabled="isToday"
              :title="$t('project-logs.next-day')"
          ><b-icon icon="chevron-right" size="is-small" /></button>
          <b-button size="is-small" v-if="!isToday" @click="goToToday">
            {{ $t('project-logs.today') }}
          </b-button>
        </div>
        <b-button
            type="is-primary"
            icon-left="content-duplicate"
            @click="duplicateLogs"
        >{{ $t('project-logs.duplicate-logs') }}</b-button>
      </div>
    </OmxCard>

    <OmxCard :title="$t('project-logs.fast-entry-title')" class="project-worklogs__fast">
      <FastProjectTaskLog
          :project_id="project_id"
          :selected_date="modelDate"
          @allLogsCreated="onAllLogsCreated"
      />
    </OmxCard>

    <OmxCard density="compact" class="project-worklogs__tabs-card">
      <b-tabs type="is-boxed" v-model="activeTab" class="project-worklogs__tabs">
        <b-tab-item :label="$t('tasks-logs.component-title')" icon="format-list-checks">
          <TasksLogs :project_id="project_id" :selected_date="selectedDate" />
        </b-tab-item>
        <b-tab-item :label="$t('personnel-worklogs.component-title')" icon="account-hard-hat">
          <PersonnelWorklogs :project_id="project_id" :selected_date="selectedDate" />
        </b-tab-item>
        <b-tab-item :label="$t('equipments-logs.component-title')" icon="robot-industrial">
          <EquipmentsLogs :project_id="project_id" :selected_date="selectedDate" />
        </b-tab-item>
        <b-tab-item :label="$t('consumables-logs.component-title')" icon="cart-variant">
          <ConsumablesLogs :project_id="project_id" :selected_date="selectedDate" />
        </b-tab-item>
      </b-tabs>
    </OmxCard>

    <b-modal
        :width="640"
        v-model="isDuplicateLogsModalActive"
        trap-focus
        :destroy-on-hide="true"
        aria-role="dialog"
        aria-modal
    >
      <template #default="props">
        <DuplicateLogsDetails
            :project_id="project_id"
            :passed_source_date="selectedDate"
            @close="props.close"
            @logsDuplicated="onDuplicateSuccess"
        />
      </template>
    </b-modal>
  </div>
</template>

<script>
import TasksLogs from "@/components/TasksLogs";
import PersonnelWorklogs from "@/components/PersonnelWorklogs";
import EquipmentsLogs from "@/components/EquipmentsLogs";
import ConsumablesLogs from "@/components/ConsumablesLogs";
import firebase from "firebase/app";
import FastProjectTaskLog from "@/components/FastProjectTaskLog";
import DuplicateLogsDetails from "@/components/DuplicateLogsDetails";
import OmxPageHeader from "@/components/OmxPageHeader.vue";
import OmxCard from "@/components/OmxCard.vue";

export default {
  name: "ProjectLogs",
  components: {
    OmxPageHeader,
    OmxCard,
    DuplicateLogsDetails,
    FastProjectTaskLog,
    TasksLogs,
    PersonnelWorklogs,
    EquipmentsLogs,
    ConsumablesLogs
  },
  props: {
    project_id: { type: String }
  },
  data() {
    return {
      modelDate: new Date(),
      projectCode: "",
      isDuplicateLogsModalActive: false,
      activeTab: 0
    };
  },
  computed: {
    pageTitle() {
      return this.$t('project-logs.page-title');
    },
    selectedDate() {
      return this.modelDate;
    },
    // Logs can't be dated in the future: cap the date picker at end of today.
    maxSelectableDate() {
      const d = new Date();
      d.setHours(23, 59, 59, 999);
      return d;
    },
    headerSubtitle() {
      return this.getFormattedDateTime(this.modelDate);
    },
    headerPills() {
      const pills = [];
      if (this.projectCode) {
        pills.push({ label: this.projectCode, tone: 'draft', dot: false });
      }
      if (this.isToday) {
        pills.push({ label: this.$t('project-logs.today'), tone: 'live' });
      }
      return pills;
    },
    isToday() {
      const d = this.modelDate;
      const t = new Date();
      return d.getDate() === t.getDate()
          && d.getMonth() === t.getMonth()
          && d.getFullYear() === t.getFullYear();
    }
  },
  methods: {
    duplicateLogs() {
      this.isDuplicateLogsModalActive = true;
    },
    readProjectData() {
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const getProjectFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getProjectFunction({ action: "get_project_by_id", document_id: this.project_id, fill_meta: true })
          .then((result) => {
            this.projectCode = result.data.projects.code;
          })
          .catch((error) => {
            this.showError(error.message);
            if (error.message === "InvalidPath") {
              this.$router.replace("../projects");
            }
          });
    },
    onDuplicateSuccess() {
      this.isDuplicateLogsModalActive = false;
    },
    onAllLogsCreated() {
      // Trigger child refresh by touching modelDate (kept from legacy logic).
      const current = new Date(this.modelDate);
      this.modelDate = new Date();
      this.modelDate = new Date(current.getTime() + 1);
    },
    dateSelectClicked(date) {
      this.modelDate = date;
    },
    shiftDate(deltaDays) {
      const d = new Date(this.modelDate);
      d.setDate(d.getDate() + deltaDays);
      // Never allow navigating to a future day — logs can't be dated ahead.
      if (d > this.maxSelectableDate) return;
      this.modelDate = d;
    },
    goToToday() {
      this.modelDate = new Date();
    },
    backClicked() {
      this.$router.back();
    }
  },
  mounted() {
    if (!this.currentUserAccess("ProjectLogs", 'VIEW', this.project_id)) {
      console.log("Page access denied.");
      this.$router.replace("/projects/").catch(() => { /* navigation duplicated */ });
    }
    this.readProjectData();
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/tokens";

.project-worklogs {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.project-worklogs__datebar-inner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.project-worklogs__date {
  display: flex;
  align-items: center;
  gap: 8px;
}

.project-worklogs__nav-btn {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid $omx-ink-200;
  background: $omx-surface;
  color: $omx-ink-700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:hover { border-color: $omx-brand-500; color: $omx-brand-600; }
}

.project-worklogs__tabs-card {
  padding: 0 !important;
  overflow: hidden;
  ::v-deep .b-tabs .tabs ul {
    border-bottom: 1px solid $omx-ink-100;
  }
  ::v-deep .b-tabs .tab-content { padding: 18px; }
}
</style>
